import { Alert, Button, Grid } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';
import { FormProvider, SubmitHandler } from 'react-hook-form';
import { Link as RouterLink, useParams } from 'react-router-dom';
import {
	usePolicyholder,
	useUpdatePolicyholderMutation,
} from '../../api/PolicyholderApi';
import { useAuthContext } from '../../context/auth';
import { useConfigContext } from '../../context/config';
import { EntityResponse } from '../../types/entityDefinitions';
import { FastForm, useFastForm } from '../fastForm';
import { FORM_NAME } from '../fastForm/data/constants';
import {
	getDefaultPolicyValues,
	getPolicyholderForm,
} from '../fastForm/formBuilders';
import { DefaultValuesType } from '../fastForm/types';
import { policyholderUpdateBuilder } from '../fastForm/updateBuilders';
import { PolicyholderLoadingForm } from './PolicyholderLoadingForm';

interface PolicyholderFormProps {
	entity: EntityResponse;
	version: string;
}

export const PolicyholderForm = (props: PolicyholderFormProps) => {
	const params = useParams();
	const { policyholderId } = params;
	const { entity, version } = props;
	const authData = useAuthContext();
	const configData = useConfigContext();
	const queryClient = useQueryClient();
	const [edit, setEdit] = useState(false);

	const authToken = authData.auth.authorizationToken;
	const timezone = authData.auth.timezone;
	const hasPermission = authData.hasPermission;

	const policyholderMutation = useUpdatePolicyholderMutation(
		policyholderId,
		authToken,
		queryClient,
	);

	const form = getPolicyholderForm({ configData, entity });
	const defaultValues = getDefaultPolicyValues(form, authData);
	const methods = useFastForm(form, defaultValues);

	const { formState, handleSubmit } = methods;
	const { dirtyFields } = formState;

	const submit: SubmitHandler<DefaultValuesType> = async (values) => {
		const request = policyholderUpdateBuilder(
			dirtyFields as any,
			values,
			form,
			FORM_NAME.policyholder,
			policyholderId,
			version,
			timezone,
		);
		policyholderMutation.mutate(request);
		setEdit(false);
	};

	return (
		<FormProvider {...methods}>
			<Grid
				container
				spacing={2}
				component='form'
				noValidate
				onSubmit={handleSubmit(submit)}
			>
				<FastForm
					formName={FORM_NAME.policyholder}
					form={form}
					configVersion=''
					productName=''
					authData={authData}
					disabled={policyholderMutation.isLoading || !edit}
				/>
				{hasPermission('policyholder.update') && (
					<Grid
						item
						xs={12}
						sx={{ display: 'flex', justifyContent: 'flex-end' }}
					>
						{edit && (
							<Button
								variant='text'
								onClick={() => setEdit(false)}
								disabled={policyholderMutation.isLoading}
							>
								Cancel
							</Button>
						)}
						{edit && (
							<Button
								variant='contained'
								type='submit'
								disabled={policyholderMutation.isLoading}
							>
								Save
							</Button>
						)}
						{!edit && (
							<Button variant='text' onClick={() => setEdit(true)}>
								Edit
							</Button>
						)}
					</Grid>
				)}
			</Grid>
		</FormProvider>
	);
};

const PolicyholderOverview = () => {
	const params = useParams();
	const { policyholderId } = params;
	const authData = useAuthContext();
	const hasPermission = authData.hasPermission;
	const authToken = authData.auth.authorizationToken;

	const {
		data: policyholderData,
		isLoading,
		isError,
	} = usePolicyholder(policyholderId, authToken);

	if (isLoading) {
		return <PolicyholderLoadingForm />;
	}

	if (isError) {
		return (
			<Grid container spacing={2} justifyContent={'flex-end'}>
				<Grid item xs={12}>
					<Alert severity='error'>
						Unable to find policyholder with locator {policyholderId}
					</Alert>
				</Grid>
			</Grid>
		);
	}

	return (
		<Grid container spacing={2} justifyContent={'flex-end'}>
			<Grid item>
				{hasPermission('auxData.fetch') && (
					<Button component={RouterLink} to='../auxData'>
						Aux Data
					</Button>
				)}
			</Grid>
			<PolicyholderForm
				entity={policyholderData.data.entity}
				version={policyholderData.data.version}
			/>
		</Grid>
	);
};

export default PolicyholderOverview;
